import React, { useState, useEffect } from 'react';
import { Search, Filter, MoreHorizontal, UserCheck, UserX, Shield, Mail, ArrowUpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '../utils';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { canManagePlatform } from '../utils/permissions';

const AdminUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user && !canManagePlatform(user)) {
      navigate('/');
      return;
    }

    const fetchUsers = async () => {
      try {
        const data = await api.get('/users');
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch users', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const toggleStatus = async (id) => {
    const user = (Array.isArray(users) ? users : []).find(u => u.id === id);
    if (!user) return;
    const newStatus = user.status === 'Active' ? 'Suspended' : 'Active';
    try {
      const updatedUser = await api.put(`/users/${id}`, { status: newStatus });
      setUsers(prev => Array.isArray(prev) ? prev.map(u => u.id === id ? updatedUser : u) : []);
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const promoteUser = async (id) => {
    const user = (Array.isArray(users) ? users : []).find(u => u.id === id);
    if (!user) return;
    const roles = ['User', 'Session Moderator', 'Community Admin', 'Platform Admin'];
    const currentIndex = roles.indexOf(user.role);
    const nextRole = roles[(currentIndex + 1) % roles.length];
    
    try {
      const updatedUser = await api.put(`/users/${id}`, { role: nextRole });
      setUsers(prev => Array.isArray(prev) ? prev.map(u => u.id === id ? updatedUser : u) : []);
    } catch (err) {
      console.error('Failed to promote user', err);
    }
  };

  const filteredUsers = (Array.isArray(users) ? users : []).filter(u => 
    (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
    (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (u.role || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight">User Management</h1>
          <p className="text-gray-500 font-medium mt-1">Manage platform users, roles, and permissions.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Search users..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-white border border-gray-100 rounded-xl text-sm focus:ring-2 focus:ring-brand-indigo/10 outline-none w-64"
            />
          </div>
          <button className="p-2 bg-white border border-gray-100 rounded-xl text-gray-600 hover:bg-gray-50 transition-all">
            <Filter size={20} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50/50 border-b border-gray-50">
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">User</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Role</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Status</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Joined</th>
              <th className="px-8 py-5 text-xxs font-bold text-gray-400 uppercase tracking-[0.15em]">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? (
              <tr>
                <td colSpan="5" className="px-8 py-10 text-center text-gray-500 font-medium">
                  Loading users...
                </td>
              </tr>
            ) : (Array.isArray(filteredUsers) && filteredUsers.length > 0) ? (
              filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50/30 transition-colors group">
                <td className="px-8 py-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gray-100 overflow-hidden shrink-0">
                      <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user.name}`} alt="" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 text-sm leading-tight">{user.name}</p>
                      <p className="text-xs text-gray-500 font-medium">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5">
                  <span className={cn(
                    "text-xxs font-bold px-2 py-1 rounded-lg uppercase tracking-[0.15em]",
                    user.role === 'Platform Admin' ? "text-purple-600 bg-purple-50" : 
                    user.role === 'Community Admin' ? "text-brand-indigo bg-brand-indigo/10" : 
                    user.role === 'Session Moderator' ? "text-brand-indigo bg-brand-indigo/10" : "text-gray-500 bg-gray-50"
                  )}>
                    {user.role}
                  </span>
                </td>
                <td className="px-8 py-5">
                  <span className={cn(
                    "flex items-center gap-1.5 text-xs font-bold",
                    user.status === 'Active' ? "text-green-600" : "text-red-600"
                  )}>
                    <span className={cn("w-1.5 h-1.5 rounded-full", user.status === 'Active' ? "bg-green-500" : "bg-red-500")} />
                    {user.status}
                  </span>
                </td>
                <td className="px-8 py-5 text-sm text-gray-500 font-medium">{user.joined}</td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => promoteUser(user.id)}
                      className="p-2 text-gray-400 hover:text-brand-indigo hover:bg-brand-indigo/5 rounded-xl transition-all active:scale-90" 
                      title="Promote Role"
                    >
                      <ArrowUpCircle size={18} />
                    </button>
                    <button 
                      onClick={() => toggleStatus(user.id)}
                      className={cn(
                        "p-2 rounded-xl transition-all active:scale-90",
                        user.status === 'Active' ? "text-gray-400 hover:text-red-600 hover:bg-red-50" : "text-green-600 hover:bg-green-50"
                      )}
                      title={user.status === 'Active' ? "Suspend User" : "Activate User"}
                    >
                      {user.status === 'Active' ? <UserX size={18} /> : <UserCheck size={18} />}
                    </button>
                    <button className="p-2 text-gray-400 hover:text-gray-900 rounded-xl">
                      <MoreHorizontal size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))) : (
              <tr>
                <td colSpan="5" className="px-8 py-10 text-center text-gray-500 font-medium">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminUsers;
